import { secureGet, secureGetFile, securePost, securePut, secureDelete } from '../utils/HttpUtils';

const apiUrls = {
  getGuaranteeTypes: '/v1/private/guarantees/guarantee-types',
  getMonetizationFormData: '/v1/private/guarantees/monetization/new',
  createCheck: '/v1/private/guarantees/monetization/new/checks',
  createLoan: '/v1/private/guarantees/monetization/new/loans',
  createPromissoryNote: '/v1/private/guarantees/monetization/new/promissory-notes',
  createCommercialEndorsement: '/v1/private/guarantees/monetization/new/commercial-endorsements',
  getMonetization: '/v1/private/guarantees/monetization',
  updateCheck: '/v1/private/guarantees/monetization/checks',
  updateLoan: '/v1/private/guarantees/monetization/loans',
  updatePromissoryNote: '/v1/private/guarantees/monetization/promissory-notes',
  updateCommercialEndorsement: '/v1/private/guarantees/monetization/commercial-endorsements',
  executeCheck: '/v1/private/guarantees/monetization/check',
  executePromissoryNote: '/v1/private/guarantees/monetization/promissory-note',
  executeLoan: '/v1/private/guarantees/monetization/loan',
  executeCommercialEndorsement: '/v1/private/guarantees/monetization/commercial-endorsement',
  uploadLoanFile: '/v1/private/guarantees/monetization/loans/upload',
  getLoanFiles: '/v1/private/guarantees/monetization/files',
  uploadFile: '/v1/private/guarantees/monetization/files',
  downloadFile: '/v1/private/guarantees/monetization/files/download',
  deleteLoanFile: '/v1/private/guarantees/monetization/loans/files',
  downloadLoanFile: '/v1/private/guarantees/monetization/loans/files',
};

export const uploadLoanMonetizationFile = async (idGlobal: number, file: FormData) => {
  try {
    const url = `${apiUrls.uploadLoanFile}/${idGlobal}`;
    return securePost(url, file, 'multipart/form-data');
  } catch (error) {
    console.error('Error uploading loan monetization file:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getGuaranteeTypes = async (monetizationTypeId?: number, monetizationTypeName?: string) => {
  try {
    const params = new URLSearchParams();
    if (monetizationTypeId) params.append('monetization_type_id', String(monetizationTypeId));
    if (monetizationTypeName) params.append('monetization_type_name', monetizationTypeName);
    const url = `${apiUrls.getGuaranteeTypes}?${params.toString()}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching guarantee types:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getMonetizationFormData = async (productLineId: string) => {
  try {
    const url = `${apiUrls.getMonetizationFormData}/${productLineId}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching monetization form data:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getMonetizationFormDataByProductLineCode = async (productLineCode: string) => {
  try {
    const url = `${apiUrls.getMonetizationFormData}?product_line_code=${encodeURIComponent(productLineCode)}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching monetization form data by code:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createMonetization = async (operationType: string, formData: any) => {
  try {
    let url: string;
    
    switch (operationType.toLowerCase()) {
      case 'préstamo':
      case 'prestamo':
        url = apiUrls.createLoan;
        break;
      case 'cheque':
        url = apiUrls.createCheck;
        break;
      case 'pagaré':
      case 'pagare':
        url = apiUrls.createPromissoryNote;
        break;
      case 'commercial-endorsements':
        url = apiUrls.createCommercialEndorsement;
        break;
      default:
        throw new Error(`Tipo de operación no soportado: ${operationType}`);
    }
    
    return securePost(url, formData);
  } catch (error) {
    console.error('Error creating monetization:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getMonetizationById = async (operationType: string, id: string) => {
  try {
    let type: string;
    
    switch (operationType.toLowerCase()) {
      case 'préstamo':
      case 'prestamo':
        type = 'loan';
        break;
      case 'cheque':
        type = 'check';
        break;
      case 'pagaré':
      case 'pagare':
        type = 'promissory_note';
        break;
      case 'commercial-endorsements':
        type = 'commercial_endorsement';
        break;
      default:
        throw new Error(`Tipo de operación no soportado: ${operationType}`);
    }
    
    const url = `${apiUrls.getMonetization}/${id}?type=${type}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching monetization:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateMonetization = async (operationType: string, id: string, formData: any) => {
  try {
    let url: string;
    
    switch (operationType.toLowerCase()) {
      case 'préstamo':
      case 'prestamo':
        url = `${apiUrls.updateLoan}/${id}`;
        break;
      case 'cheque':
        url = `${apiUrls.updateCheck}/${id}`;
        break;
      case 'pagaré':
      case 'pagare':
        url = `${apiUrls.updatePromissoryNote}/${id}`;
        break;
      case 'commercial-endorsements':
        url = `${apiUrls.updateCommercialEndorsement}/${id}`;
        break;
      default:
        throw new Error(`Tipo de operación no soportado: ${operationType}`);
    }
    
    return securePut(url, formData);
  } catch (error) {
    console.error('Error updating monetization:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const executeMonetization = async (operationType: string, id: string, formData?: any) => {
  try {
    let url: string;
    
    switch (operationType.toLowerCase()) {
      case 'préstamo':
      case 'prestamo':
        url = `${apiUrls.executeLoan}/${id}/execute`;
        break;
      case 'cheque':
        url = `${apiUrls.executeCheck}/${id}/execute`;
        break;
      case 'pagaré':
      case 'pagare':
        url = `${apiUrls.executePromissoryNote}/${id}/execute`;
        break;
      case 'commercial-endorsements':
        url = `${apiUrls.executeCommercialEndorsement}/${id}/execute`;
        break;
      default:
        throw new Error(`Tipo de operación no soportado: ${operationType}`);
    }
    
    return securePut(url, formData || {});
  } catch (error) {
    console.error('Error executing monetization:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getLoanMonetizationFiles = async (idGlobal: string) => {
  try {
    const url = `${apiUrls.getLoanFiles}/${idGlobal}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching loan monetization files:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const downloadLoanMonetizationFile = async (idGlobal: string) => {
  try {
    const url = `${apiUrls.downloadLoanFile}/${idGlobal}`;
    return secureGetFile(url);
  } catch (error) {
    console.error('Error downloading loan monetization file:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const uploadMonetizationFile = async (idGlobal: string, formData: FormData) => {
  try {
    const url = `${apiUrls.uploadFile}/${idGlobal}`;
    return securePost(url, formData, 'multipart/form-data');
  } catch (error) {
    console.error('Error uploading monetization file:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const downloadMonetizationFile = async (idGlobal: string, fileName: string) => {
  try {
    const url = `${apiUrls.downloadFile}/${idGlobal}?fileName=${encodeURIComponent(fileName)}`;
    return secureGetFile(url);
  } catch (error) {
    console.error('Error downloading monetization file:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const deleteLoanMonetizationFile = async (idGlobal: string, fileName: string) => {
  try {
    const url = `${apiUrls.deleteLoanFile}/${idGlobal}?fileName=${fileName}`;
    return secureDelete(url);
  } catch (error) {
    console.error('Error deleting loan monetization file:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const downloadMonetizationPdf = async (idGlobal: string) => {
  try {
    const url = `/v1/private/guarantees/monetization/${idGlobal}/download`;
    console.log('Calling PDF download endpoint:', url);
    const response = await secureGetFile(url);
    console.log('PDF download response received:', { success: response.success, hasData: 'data' in response && !!response.data });
    return response;
  } catch (error) {
    console.error('Error downloading monetization PDF:', JSON.stringify(error, null, 2));
    throw error;
  }
};